"use client"
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Breadcrumb } from "../../components/ui/breadcrumb";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { useEspeces } from "../../hooks/api/especes";
import { PageHeader } from "../../ui/layout/page-header";
import { EspeceDetail } from "./espece-detail";
import { EspeceForm } from "./espece-form";
import { EspeceList } from "./espece-list";

enum View {
  LIST = "list",
  DETAIL = "detail",
  CREATE = "create",
  EDIT = "edit",
}

export function EspeceManagement() {
  const [currentView, setCurrentView] = useState<View>(View.LIST);
  const [selectedEspeceId, setSelectedEspeceId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("genre");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, showDeleted, sortBy]);

  const { data: especesData, isLoading, error } = useEspeces({
    page,
    limit,
    search: debouncedSearch || undefined,
    includeDeleted: showDeleted,
    sortBy,
  });

  const handleCreateClick = () => {
    setCurrentView(View.CREATE);
    setSelectedEspeceId(null);
  };

  const handleEditClick = (id: number) => {
    setSelectedEspeceId(id);
    setCurrentView(View.EDIT);
  };

  const handleViewClick = (id: number) => {
    setSelectedEspeceId(id);
    setCurrentView(View.DETAIL);
  };

  const handleBackToList = () => {
    setCurrentView(View.LIST);
    setSelectedEspeceId(null);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setPage(1);
  };

  const onPageChange = (page: number) => setPage(page);

  const onPageSizeChange = (size: number) => {
    setLimit(size);
    setPage(1);
  };

  const handleShowDeletedChange = (show: boolean) => setShowDeleted(show);

  const handleSortChange = (sort: string) => {
    setSortBy(sort);
    setPage(1);
  };

  const renderView = () => {
    switch (currentView) {
      case View.CREATE:
        return <EspeceForm onCancel={handleBackToList} onSuccess={handleBackToList} />;

      case View.EDIT:
        return selectedEspeceId ? (
          <EspeceForm
            key={`edit-espece-${selectedEspeceId}`}
            especeId={selectedEspeceId}
            onCancel={handleBackToList}
            onSuccess={handleBackToList}
          />
        ) : null;

      case View.DETAIL:
        return selectedEspeceId ? (
          <EspeceDetail
            especeId={selectedEspeceId}
            onBack={handleBackToList}
            onEdit={() => handleEditClick(selectedEspeceId)}
          />
        ) : null;

      case View.LIST:
      default:
        return (
          <EspeceList
            especes={especesData?.data || []}
            isLoading={isLoading}
            error={error}
            onView={handleViewClick}
            onEdit={handleEditClick}
            page={page}
            limit={limit}
            total={especesData?.meta.total || 0}
            pageCount={especesData?.meta.totalPages || 0}
            sortBy={sortBy}
            onLimitChange={onPageSizeChange}
            onSearchChange={handleSearch}
            searchTerm={searchTerm}
            onPageChange={onPageChange}
            showDeleted={showDeleted}
            onShowDeletedChange={handleShowDeletedChange}
            handleSortChange={handleSortChange}
          />
        );
    }
  };

  return (
    <div className="space-y-3">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          {
            label: "Gestion",
            onClick: () => { if (currentView !== View.LIST) handleBackToList(); },
          },
          {
            label: "Espèces",
            onClick: () => { if (currentView !== View.LIST) handleBackToList(); },
          },
          ...(currentView === View.DETAIL && selectedEspeceId
            ? [{ label: "Détail", current: true }]
            : []),
          ...(currentView === View.CREATE
            ? [{ label: "Créer", current: true }]
            : []),
          ...(currentView === View.EDIT && selectedEspeceId
            ? [{ label: "Modifier", current: true }]
            : []),
        ]}
        showHomeIcon
        onHomeClick={() => { if (currentView !== View.LIST) handleBackToList(); }}
        className="mb-1"
      />

      {/* Page header + action button — list view only */}
      {currentView === View.LIST && (
        <div className="flex items-start justify-between gap-4">
          <PageHeader className="mb-0 space-y-1">
            <PageHeader.Title>Gestion des Espèces</PageHeader.Title>
            <PageHeader.Description>
              Gérez le référentiel des espèces botaniques et leurs caractéristiques.
            </PageHeader.Description>
          </PageHeader>
          <Button onClick={handleCreateClick} className="shrink-0">
            <Plus className="h-4 w-4 mr-1.5" />
            Créer une espèce
          </Button>
        </div>
      )}

      {/* Content card */}
      <Card className="p-3">
        {renderView()}
      </Card>
    </div>
  );
}

export { EspeceDetail } from './espece-detail';
export { EspeceForm } from './espece-form';
export { EspeceList } from './espece-list';
export { EspeceSelect, type EspeceSelectOption } from './components/espece-select';
export { especesToSelectOptions } from './utils/espece-select-utils';
